/**
 * Monitoring expectations: pure functions shared by the Worker (diagnostics) and the client (loop detail).
 *
 * An expectation is an inference, never a fact about the source system. Every result names its basis
 * (cadence text, schedule note, or observed run spacing) so the UI and agents can label it honestly.
 * Weekday handling uses UTC calendar days.
 */

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

export type IntervalBasis = "cadence" | "schedule" | "observed";

export type ExpectedInterval = {
  intervalMs: number | null;
  basis: IntervalBasis | null;
  weekdaysOnly: boolean;
};

const NO_EXPECTATION: ExpectedInterval = { intervalMs: null, basis: null, weekdaysOnly: false };

type ParsedText =
  | { kind: "interval"; intervalMs: number; weekdaysOnly: boolean }
  | { kind: "irregular" }
  | { kind: "unparsed" };

const IRREGULAR = /\b(continuous(?:ly)?|streaming|always[- ]on|event[- ]?driven|on[- ]?demand|ad[- ]?hoc|manual(?:ly)?|triggered|webhook)\b/u;
const WEEKDAYS = /\b(weekdays?|business days|working days|mon(?:day)?\s*(?:-|–|—|to|through)\s*fri(?:day)?)\b/u;
const DAY_NAMES = /\b(?:every|each|on)\s+(?:mon|tues?|wed(?:nes)?|thu(?:rs)?|fri|sat(?:ur)?|sun)(?:day)?s?\b/u;
const EVERY_N = /\bevery\s+(\d+(?:\.\d+)?)\s*(minutes?|mins?|m|hours?|hrs?|h|days?|d|weeks?|wks?|w)\b/u;
/** "every two days", "every three hours": number words up to twelve, as agents often write them. */
const EVERY_WORD = /\bevery\s+(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+(minutes?|hours?|days?|weeks?)\b/u;
const NUMBER_WORDS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
const CRON = /(?:^|\s)((?:[\d*\/,\-]+))\s+((?:[\d*\/,\-]+))\s+((?:[\d*\/,\-?LW]+))\s+((?:[\d*\/,\-]+|[a-z]{3}(?:-[a-z]{3})?))\s+((?:[\d*\/,\-?L#]+|[a-z]{3}(?:-[a-z]{3})?(?:,[a-z]{3})*))(?=\s|$|\))/u;

function normalize(value: string | null | undefined): string {
  return (value ?? "").toLowerCase().replace(/\s+/gu, " ").trim();
}

function unitMs(unit: string): number {
  if (unit.startsWith("w")) return 7 * DAY;
  if (unit.startsWith("d")) return DAY;
  if (unit.startsWith("h")) return HOUR;
  return MINUTE;
}

/** Free-form cadence or schedule text such as "daily", "weekdays 07:30", "every 6 hours". */
function parseText(value: string): ParsedText {
  const text = normalize(value);
  if (!text) return { kind: "unparsed" };
  if (IRREGULAR.test(text)) return { kind: "irregular" };
  const weekdaysOnly = WEEKDAYS.test(text);

  const everyN = EVERY_N.exec(text);
  if (everyN) {
    const amount = Number(everyN[1]);
    if (Number.isFinite(amount) && amount > 0) return { kind: "interval", intervalMs: Math.round(amount * unitMs(everyN[2])), weekdaysOnly };
  }
  const everyWord = EVERY_WORD.exec(text);
  if (everyWord) return { kind: "interval", intervalMs: NUMBER_WORDS[everyWord[1]] * unitMs(everyWord[2]), weekdaysOnly };
  if (/\b(every other day|alternate days)\b/u.test(text)) return { kind: "interval", intervalMs: 2 * DAY, weekdaysOnly };
  if (/\b(annual(?:ly)?|yearly|every year|once a year|each year)\b/u.test(text)) return { kind: "interval", intervalMs: 365 * DAY, weekdaysOnly };
  if (/\b(quarterly|every quarter|each quarter)\b/u.test(text)) return { kind: "interval", intervalMs: 91 * DAY, weekdaysOnly };
  if (/\b(twice (?:a |per )?(?:day|daily)|two times (?:a |per )?day)\b/u.test(text)) return { kind: "interval", intervalMs: 12 * HOUR, weekdaysOnly };
  if (/\b(hourly|every hour|each hour|once an hour)\b/u.test(text)) return { kind: "interval", intervalMs: HOUR, weekdaysOnly };
  if (/\b(bi-?weekly|fortnightly|every two weeks|every second week)\b/u.test(text)) return { kind: "interval", intervalMs: 14 * DAY, weekdaysOnly };
  if (/\b(weekly|every week|each week|once a week)\b/u.test(text) || DAY_NAMES.test(text)) return { kind: "interval", intervalMs: 7 * DAY, weekdaysOnly };
  if (/\b(monthly|every month|each month|once a month)\b/u.test(text)) return { kind: "interval", intervalMs: 30 * DAY, weekdaysOnly };
  if (weekdaysOnly) return { kind: "interval", intervalMs: DAY, weekdaysOnly: true };
  if (/\b(daily|every day|each day|nightly|every night|every morning|every evening|once a day|per day)\b/u.test(text)) return { kind: "interval", intervalMs: DAY, weekdaysOnly };
  return { kind: "unparsed" };
}

function cronFieldCount(field: string, max: number): number | null {
  if (field === "*") return null;
  let count = 0;
  for (const part of field.split(",")) {
    const range = /^(\d+)-(\d+)$/u.exec(part);
    if (range) {
      const [start, end] = [Number(range[1]), Number(range[2])];
      if (start > end || end > max) return null;
      count += end - start + 1;
    } else if (/^\d+$/u.test(part) && Number(part) <= max) {
      count += 1;
    } else {
      return null;
    }
  }
  return count;
}

const DOW_NAMES: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

function normalizeDow(field: string): string {
  return field
    .split(",")
    .map((part) => part.replace(/[a-z]{3}/gu, (name) => String(DOW_NAMES[name] ?? name)))
    .join(",");
}

/**
 * Supports the common shapes of a five-field cron: `*​/N` minutes or hours, fixed hours with any day-of-week
 * selection, and a fixed day of the month. Anything else is left alone rather than guessed.
 */
function parseCron(value: string): { intervalMs: number; weekdaysOnly: boolean } | null {
  const match = CRON.exec(normalize(value));
  if (!match) return null;
  const [, minute, hour, dayOfMonth, month, rawDow] = match;
  if (month !== "*") return null;
  const dow = normalizeDow(rawDow);
  const stepMinute = /^\*\/(\d+)$/u.exec(minute);
  const stepHour = /^\*\/(\d+)$/u.exec(hour);
  if (stepMinute && hour === "*" && dayOfMonth === "*" && dow === "*") {
    const step = Number(stepMinute[1]);
    return step > 0 && step < 60 ? { intervalMs: step * MINUTE, weekdaysOnly: false } : null;
  }
  if (stepHour && /^\d+$/u.test(minute) && dayOfMonth === "*" && dow === "*") {
    const step = Number(stepHour[1]);
    return step > 0 && step < 24 ? { intervalMs: step * HOUR, weekdaysOnly: false } : null;
  }
  if (!/^\d+$/u.test(minute)) return null;
  const hourCount = cronFieldCount(hour, 23);
  if (hourCount == null) return null;
  if (dayOfMonth !== "*") {
    return cronFieldCount(dayOfMonth, 31) === 1 && dow === "*" ? { intervalMs: 30 * DAY, weekdaysOnly: false } : null;
  }
  if (dow === "*") return { intervalMs: Math.round(DAY / hourCount), weekdaysOnly: false };
  if (dow === "1-5") return { intervalMs: Math.round(DAY / hourCount), weekdaysOnly: true };
  const dowCount = cronFieldCount(dow, 7);
  if (dowCount == null || dowCount === 0) return null;
  return { intervalMs: Math.round((7 * DAY) / (dowCount * hourCount)), weekdaysOnly: false };
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/** Median gap between the last ten distinct starts, accepted only when the gaps are reasonably regular. */
function observeInterval(runStartsIso: string[]): number | null {
  const starts = Array.from(new Set(runStartsIso.map((value) => new Date(value).getTime()).filter((value) => Number.isFinite(value))))
    .sort((a, b) => a - b)
    .slice(-10);
  if (starts.length < 3) return null;
  const gaps = starts.slice(1).map((value, index) => value - starts[index]).filter((gap) => gap > 0);
  if (gaps.length < 2) return null;
  const typical = median(gaps);
  const regular = gaps.filter((gap) => gap >= typical / 2 && gap <= typical * 2).length;
  return regular / gaps.length >= 0.75 ? Math.round(typical) : null;
}

export function inferExpectedInterval(input: { cadence?: string | null; scheduleHint?: string | null; runStartsIso?: string[] }): ExpectedInterval {
  const cron = input.scheduleHint ? parseCron(input.scheduleHint) : null;
  if (cron) return { intervalMs: cron.intervalMs, basis: "schedule", weekdaysOnly: cron.weekdaysOnly };

  const cadence = parseText(input.cadence ?? "");
  if (cadence.kind === "irregular") return NO_EXPECTATION;
  const hint = parseText(input.scheduleHint ?? "");
  if (cadence.kind === "interval") {
    const hintWeekdays = hint.kind === "interval" && hint.weekdaysOnly && hint.intervalMs === cadence.intervalMs;
    return { intervalMs: cadence.intervalMs, basis: "cadence", weekdaysOnly: cadence.weekdaysOnly || hintWeekdays };
  }
  if (hint.kind === "irregular") return NO_EXPECTATION;
  if (hint.kind === "interval") return { intervalMs: hint.intervalMs, basis: "schedule", weekdaysOnly: hint.weekdaysOnly };

  const observed = input.runStartsIso ? observeInterval(input.runStartsIso) : null;
  return observed ? { intervalMs: observed, basis: "observed", weekdaysOnly: false } : NO_EXPECTATION;
}

function isWeekend(ms: number): boolean {
  const day = new Date(ms).getUTCDay();
  return day === 0 || day === 6;
}

function startOfNextUtcDay(ms: number): number {
  const date = new Date(ms);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1);
}

/** Adds `durationMs` of weekday time to `startMs`, skipping Saturdays and Sundays (UTC). */
function addWeekdayTime(startMs: number, durationMs: number): number {
  let at = startMs;
  let remaining = durationMs;
  while (remaining > 0) {
    if (isWeekend(at)) {
      at = startOfNextUtcDay(at);
      continue;
    }
    const step = Math.min(remaining, startOfNextUtcDay(at) - at);
    at += step;
    remaining -= step;
  }
  while (isWeekend(at) && durationMs > 0) at = startOfNextUtcDay(at);
  return at;
}

export function detectMissedRun(input: {
  nowIso: string;
  lastRunStartIso: string | null | undefined;
  intervalMs: number | null;
  weekdaysOnly?: boolean;
  tolerance?: number;
}): { missed: boolean; expectedByIso: string | null; overdueMs: number } {
  const now = new Date(input.nowIso).getTime();
  const lastStart = input.lastRunStartIso ? new Date(input.lastRunStartIso).getTime() : Number.NaN;
  if (!input.intervalMs || input.intervalMs <= 0 || !Number.isFinite(now) || !Number.isFinite(lastStart)) {
    return { missed: false, expectedByIso: null, overdueMs: 0 };
  }
  const tolerance = input.tolerance ?? 1.5;
  const add = input.weekdaysOnly ? addWeekdayTime : (start: number, duration: number) => start + duration;
  const expectedBy = add(lastStart, input.intervalMs);
  const deadline = add(lastStart, input.intervalMs * tolerance);
  return {
    missed: now >= deadline,
    expectedByIso: new Date(expectedBy).toISOString(),
    overdueMs: Math.max(0, now - expectedBy),
  };
}

export function detectSlowRun(input: {
  latestDurationMs: number | null | undefined;
  previousDurationsMs: Array<number | null | undefined>;
}): { slow: boolean; medianMs: number | null; ratio: number | null } {
  const previous = input.previousDurationsMs.filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0);
  if (previous.length < 3) return { slow: false, medianMs: null, ratio: null };
  const medianMs = median(previous);
  const latest = input.latestDurationMs;
  if (typeof latest !== "number" || !Number.isFinite(latest) || medianMs <= 0) return { slow: false, medianMs, ratio: null };
  const ratio = latest / medianMs;
  return { slow: ratio > 2, medianMs, ratio };
}

export type WindowMetrics = { runs: number; successes: number; failures: number; resolvedRuns: number; costUsd: number };

export type WindowComparison = {
  comparable: boolean;
  runsDelta: number;
  successRateDelta: number | null;
  costDelta: number;
};

/** Both windows need at least three resolved outcomes before a reliability delta is stated. */
export function compareWindows(current: WindowMetrics, prior: WindowMetrics): WindowComparison {
  const comparable = current.resolvedRuns >= 3 && prior.resolvedRuns >= 3;
  const rate = (window: WindowMetrics) => (window.resolvedRuns > 0 ? window.successes / window.resolvedRuns : null);
  const currentRate = rate(current);
  const priorRate = rate(prior);
  const successRateDelta = comparable && currentRate != null && priorRate != null ? Math.round((currentRate - priorRate) * 1000) / 10 : null;
  return {
    comparable,
    runsDelta: current.runs - prior.runs,
    successRateDelta,
    costDelta: Math.round((current.costUsd - prior.costUsd) * 10_000) / 10_000,
  };
}

/** "30m", "6h", "24h", "1h 30m", "7d". Whole days from two days up are written in days. */
export function describeInterval(ms: number): string {
  if (ms >= 2 * DAY && ms % DAY === 0) return `${ms / DAY}d`;
  const hours = Math.floor(ms / HOUR);
  const minutes = Math.round((ms % HOUR) / MINUTE);
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

/** "800ms", "4.2s", "42s", "1m 5s", "1h 30m". */
export function describeDuration(ms: number): string {
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  if (ms < 10_000) return `${(ms / 1_000).toFixed(1)}s`;
  if (ms < 60_000) return `${Math.round(ms / 1_000)}s`;
  if (ms < HOUR) {
    const minutes = Math.floor(ms / MINUTE);
    const seconds = Math.round((ms % MINUTE) / 1_000);
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(ms / HOUR);
  const minutes = Math.round((ms % HOUR) / MINUTE);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

/** "3d 4h", "31h", "45m". Short elapsed-time phrases for finding details. */
export function describeElapsed(ms: number): string {
  if (ms < HOUR) return `${Math.max(1, Math.round(ms / MINUTE))}m`;
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)}h`;
  const days = Math.floor(ms / DAY);
  const hours = Math.round((ms % DAY) / HOUR);
  return hours ? `${days}d ${hours}h` : `${days}d`;
}

const BASIS_LABEL: Record<IntervalBasis, string> = {
  cadence: "its cadence",
  schedule: "its schedule note",
  observed: "its observed run spacing",
};

/** "expected about every 24h on weekdays from its schedule note", or null when nothing was inferred. */
export function describeExpectation(expectation: ExpectedInterval): string | null {
  if (!expectation.intervalMs || !expectation.basis) return null;
  return `expected about every ${describeInterval(expectation.intervalMs)}${expectation.weekdaysOnly ? " on weekdays" : ""} from ${BASIS_LABEL[expectation.basis]}`;
}
